<template>
  <div class="review-confirm-step">
    <div class="text-body-2 text--secondary mb-4">
      Review your API data source configuration before saving.
    </div>

    <!-- API Configuration Card -->
    <v-card outlined class="mb-3">
      <v-card-subtitle class="pb-1 d-flex align-center">
        <v-icon small class="mr-2">mdi-api</v-icon>
        <span class="font-weight-medium">API Configuration</span>
      </v-card-subtitle>
      <v-card-text class="pt-0">
        <v-simple-table dense>
          <tbody>
            <tr>
              <td class="text-caption text--secondary" width="140">Name</td>
              <td class="font-weight-medium">{{ config.name || '—' }}</td>
            </tr>
            <tr>
              <td class="text-caption text--secondary">URL</td>
              <td class="text-truncate" style="max-width: 400px">
                <v-chip x-small outlined class="mr-1">{{ config.httpMethod }}</v-chip>
                {{ config.url || '—' }}
              </td>
            </tr>
            <tr>
              <td class="text-caption text--secondary">Authentication</td>
              <td>{{ authTypeText }}</td>
            </tr>
            <tr>
              <td class="text-caption text--secondary">Data Path</td>
              <td class="code-font">{{ config.responseDataPath || '(root)' }}</td>
            </tr>
            <tr v-if="config.pagination && config.pagination.type > 0">
              <td class="text-caption text--secondary">Pagination</td>
              <td>{{ paginationText }}</td>
            </tr>
            <tr v-if="config.headers && config.headers.length">
              <td class="text-caption text--secondary">Headers</td>
              <td>{{ config.headers.length }} custom header(s)</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <!-- Column Mapping Card -->
    <v-card outlined class="mb-3">
      <v-card-subtitle class="pb-1 d-flex align-center">
        <v-icon small class="mr-2">mdi-link-variant</v-icon>
        <span class="font-weight-medium">Column Mapping</span>
      </v-card-subtitle>
      <v-card-text class="pt-0">
        <div v-if="mapping">
          <div class="d-flex align-center mb-1">
            <span class="text-caption text--secondary">
              {{ mappedFieldCount }} field(s) mapped
              <span v-if="mapping.identityColumn">
                &middot; Identity: <strong>{{ mapping.identityColumn }}</strong>
              </span>
              <span v-if="mapping.tagColumn">
                &middot; Tag: <strong>{{ mapping.tagColumn }}</strong>
              </span>
            </span>
            <v-btn
              x-small
              text
              class="ml-2"
              @click="showMappingList = !showMappingList"
            >
              {{ showMappingList ? 'Hide' : 'Show' }} details
              <v-icon x-small right>
                {{ showMappingList ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-simple-table v-if="showMappingList" dense class="mapping-table">
              <thead>
                <tr>
                  <th class="text-caption">API Column</th>
                  <th class="text-caption">{{ targetFieldLabel }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(fieldName, colName) in mapping.fieldMappings"
                  :key="colName"
                >
                  <td class="text-caption">{{ colName }}</td>
                  <td class="text-caption font-weight-medium">{{ fieldName }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-expand-transition>
        </div>
        <div v-else class="text-caption text--secondary">
          No mapping configured
        </div>
      </v-card-text>
    </v-card>

    <!-- Schedule Card -->
    <v-card outlined class="mb-3">
      <v-card-subtitle class="pb-1 d-flex align-center">
        <v-icon small class="mr-2">mdi-clock-outline</v-icon>
        <span class="font-weight-medium">Schedule</span>
      </v-card-subtitle>
      <v-card-text class="pt-0">
        <div class="d-flex align-center">
          <v-chip
            small
            :color="schedule.frequency > 0 ? 'primary' : 'grey'"
            :outlined="schedule.frequency === 0"
          >
            {{ frequencyText }}
          </v-chip>
          <span v-if="schedule.triggerImmediate" class="ml-2 text-caption text--secondary">
            <v-icon x-small>mdi-lightning-bolt</v-icon> First import runs immediately
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Validation Warnings -->
    <v-alert
      v-if="!mapping || mappedFieldCount === 0"
      type="warning"
      outlined
      dense
      class="mt-3"
    >
      No fields are mapped. The data source will be saved but imports will fail until mapping is configured.
    </v-alert>
  </div>
</template>

<script>
import {
  getAuthTypeText,
  getScheduleFrequencyText,
  PaginationTypeOptions,
} from '@/features/Share/config/apiDataSourceEnums';

export default {
  name: 'SharedReviewConfirmStep',
  props: {
    config: {
      type: Object,
      required: true,
    },
    mapping: {
      type: Object,
      default: null,
    },
    schedule: {
      type: Object,
      required: true,
    },
    /** Label for the target field column in the mapping table (e.g. 'Register Field', 'System Field') */
    targetFieldLabel: {
      type: String,
      default: 'Target Field',
    },
  },
  data() {
    return {
      showMappingList: false,
    };
  },
  computed: {
    authTypeText() {
      return getAuthTypeText(this.config.auth?.authType) || 'None';
    },
    frequencyText() {
      return getScheduleFrequencyText(this.schedule.frequency) || 'Manual Only';
    },
    paginationText() {
      if (!this.config.pagination) return 'None';
      const found = PaginationTypeOptions.find(o => o.value === this.config.pagination.type);
      return found ? found.text : 'None';
    },
    mappedFieldCount() {
      if (!this.mapping || !this.mapping.fieldMappings) return 0;
      return Object.keys(this.mapping.fieldMappings).length;
    },
  },
};
</script>

<style scoped>
.code-font {
  font-family: 'Roboto Mono', monospace;
  font-size: 13px;
}
.mapping-table {
  max-height: 240px;
  overflow-y: auto;
}
</style>
